import { useState, useEffect } from 'react';
import api from '../../services/api';
import Table, { THead, TH, TBody, TR, TD, Pagination } from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import { formatDateTime } from '../../utils/formatDate';

const ACTION_COLORS = {
  certificate_issued: 'cyan',
  certificate_shipped: 'blue',
  certificate_delivered: 'purple',
  certificate_revoked: 'red',
  appliance_activated: 'green',
  warranty_requested: 'yellow',
  payment_confirmed: 'green',
  user_created: 'blue',
  user_login: 'gray',
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const params = new URLSearchParams({ page, limit: 20 });
        if (filterAction) params.append('action', filterAction);
        const { data } = await api.get(`/admin/audit?${params}`);
        setLogs(data.logs);
        setTotalPages(data.totalPages);
      } catch (err) {
        console.error('Load audit error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [page, filterAction]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const actions = ['', 'certificate_issued', 'certificate_shipped', 'certificate_delivered', 'appliance_activated', 'warranty_requested', 'payment_confirmed', 'user_created', 'user_login'];

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-100 mb-2">Audit Log</h1>
      <p className="text-slate-400 mb-6">Complete activity log for the system</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {actions.map(a => (
          <button
            key={a}
            onClick={() => { setFilterAction(a); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filterAction === a ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30' : 'text-slate-400 border border-navy-700 hover:border-navy-600'
            }`}
          >
            {a ? a.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      <Table>
        <THead>
          <tr><TH>Timestamp</TH><TH>User</TH><TH>Action</TH><TH>Entity</TH><TH>Details</TH><TH>IP</TH></tr>
        </THead>
        <TBody>
          {logs.map(log => (
            <TR key={log.id}>
              <TD><span className="text-xs">{formatDateTime(log.createdAt)}</span></TD>
              <TD>{log.user?.name || 'System'}</TD>
              <TD><Badge color={ACTION_COLORS[log.action] || 'gray'}>{log.action.replace(/_/g, ' ')}</Badge></TD>
              <TD><span className="text-xs text-slate-400">{log.entityType} #{log.entityId}</span></TD>
              <TD>
                {log.details && (
                  <span className="text-xs text-slate-400">
                    {typeof log.details === 'object' ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ') : String(log.details)}
                  </span>
                )}
              </TD>
              <TD><span className="text-xs text-slate-500 font-mono">{log.ipAddress || '—'}</span></TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
